/**
 * ParallaxLayer Component
 * =======================
 * Component for creating parallax scrolling effects.
 */

import React, { useRef, useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { prefersReducedMotion } from '../../utils/helpers';

gsap.registerPlugin(ScrollTrigger);

/**
 * ParallaxLayer Component
 * Creates a parallax effect on its children based on scroll position.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Content to parallax
 * @param {number} [props.speed=0.5] - Parallax speed (0 = no movement, 1 = full movement)
 * @param {string} [props.direction='vertical'] - Parallax direction (vertical/horizontal)
 * @param {string} [props.start='top bottom'] - ScrollTrigger start position
 * @param {string} [props.end='bottom top'] - ScrollTrigger end position
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} ParallaxLayer component
 */
export function ParallaxLayer({
  children,
  speed = 0.5,
  direction = 'vertical',
  start = 'top bottom',
  end = 'bottom top',
  className = '',
}) {
  const elementRef = useRef(null);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    
    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) return;
    
    const movement = direction === 'vertical' ? 'y' : 'x';
    const movementAmount = speed * 100;
    
    const animation = gsap.fromTo(
      element,
      { [movement]: -movementAmount },
      {
        [movement]: movementAmount,
        ease: 'none',
        scrollTrigger: {
          trigger: element,
          start,
          end,
          scrub: true,
        },
      }
    );
    
    return () => {
      animation.scrollTrigger?.kill();
      animation.kill();
    };
  }, [speed, direction, start, end]);
  
  return (
    <div ref={elementRef} className={`will-change-transform ${className}`}>
      {children}
    </div>
  );
}

/**
 * ParallaxContainer Component
 * Container for multiple parallax layers with z-index management.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Parallax layers
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} ParallaxContainer component
 */
export function ParallaxContainer({ children, className = '' }) {
  return (
    <div className={`relative overflow-hidden ${className}`}>
      {children}
    </div>
  );
}

/**
 * FloatingElement Component
 * Element that floats with a gentle animation.
 * 
 * @param {object} props - Component props
 * @param {React.ReactNode} props.children - Content to float
 * @param {number} [props.amplitude=30] - Float amplitude in pixels
 * @param {number} [props.duration=20] - Animation duration in seconds
 * @param {number} [props.delay=0] - Animation delay in seconds
 * @param {string} [props.className] - Additional CSS classes
 * @returns {JSX.Element} FloatingElement component
 */
export function FloatingElement({
  children,
  amplitude = 30,
  duration = 20,
  delay = 0,
  className = '',
}) {
  const elementRef = useRef(null);
  
  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;
    
    // Skip animation if user prefers reduced motion
    if (prefersReducedMotion()) return;
    
    const animation = gsap.to(element, {
      y: -amplitude,
      rotation: 5,
      duration: duration / 2,
      ease: 'sine.inOut',
      yoyo: true,
      repeat: -1,
      delay,
    });
    
    return () => {
      animation.kill();
    };
  }, [amplitude, duration, delay]);
  
  return (
    <div ref={elementRef} className={`will-change-transform ${className}`}>
      {children}
    </div>
  );
}

export default ParallaxLayer;
